import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCheck } from 'lucide-react';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Input';
import { Banner } from '@/components/ui/Banner';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/StatusBadge';
import { useToast } from '@/components/ui/Toast';
import { useAuthStore } from '@/stores/auth';
import { useNotificationsCountStore } from '@/stores/notifications-count';
import {
  notificationsApi,
  PRIORITY_LABELS,
  PRIORITY_TONES,
  TYPE_LABELS,
  type NotificationFilters,
  type NotificationItem,
  type NotificationPriority,
  type NotificationType,
} from '@/services/notifications.api';
import { formatRelative } from '@/components/TimelineSection';
import { cn } from '@/lib/utils';

/**
 * Page notifications complète - vue type agenda : regroupement par jour,
 * filtrable par type / priorité / statut lu/non-lu. Un clic sur une notif
 * la marque lue et navigue vers son lien.
 */
export function NotificationsPage() {
  const token = useAuthStore((s) => s.accessToken)!;
  const role = useAuthStore((s) => s.user?.role);

  // Types de notifications recus selon le role, documentes dans le sous-titre
  // pour que l'utilisateur sache quoi attendre. Ref. triggers :
  // - prospect_assigned, task_assigned_by_direction : tous roles assignes
  // - task_due / task_due_soon / task_overdue : destinataire = assignee
  // - opportunity_stale, quote_pending : commercial responsable de l'opp
  // - commission_threshold : admin + director UNIQUEMENT
  // - executive : aucun trigger ne cible ce role (observateur pur)
  const isDirection = role === 'admin' || role === 'director';
  const isExecutive = role === 'executive';
  const subtitle = isExecutive
    ? 'Consultation uniquement.'
    : isDirection
      ? 'Affectations de prospects · tâches assignées · échéances et retards de tâches · opportunités stagnantes · devis en attente · rappels de résiliation · dépassements de seuil de commission.'
      : 'Affectations de prospects · tâches assignées · échéances et retards de tâches · opportunités stagnantes · devis en attente · rappels de résiliation.';
  const navigate = useNavigate();
  const toast = useToast();
  const decrementNotifCount = useNotificationsCountStore((s) => s.decrement);
  const resetNotifCount = useNotificationsCountStore((s) => s.reset);
  const refreshNotifCount = useNotificationsCountStore((s) => s.refresh);
  const [items, setItems] = useState<NotificationItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [readFilter, setReadFilter] = useState<string>('');

  async function load() {
    setError(null);
    try {
      const filters: NotificationFilters = { limit: 100 };
      if (typeFilter) filters.type = typeFilter as NotificationType;
      if (priorityFilter) filters.priority = priorityFilter as NotificationPriority;
      if (readFilter) filters.is_read = readFilter as 'true' | 'false';
      const res = await notificationsApi.list(token, filters);
      setItems(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, priorityFilter, readFilter]);

  async function handleClick(n: NotificationItem) {
    try {
      if (!n.isRead) {
        await notificationsApi.markRead(token, n.id);
        decrementNotifCount();
      }
      if (n.link) navigate(n.link);
    } catch {
      /* silencieux */
    }
  }

  async function handleReadAll() {
    try {
      const res = await notificationsApi.markAllRead(token);
      resetNotifCount();
      toast({ tone: 'success', message: `${res.updated} notification(s) marquée(s) comme lues` });
      await load();
    } catch (err) {
      // En cas d'échec, on resync depuis le serveur pour ne pas laisser un
      // badge qui ment (par ex. si le reset optimiste n'a pas abouti).
      void refreshNotifCount(token);
      toast({
        tone: 'danger',
        title: 'Action impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  // Regroupement par jour (UTC local) - vue agenda type timeline.
  const grouped = useMemo(() => {
    if (!items) return [] as Array<{ day: string; label: string; items: NotificationItem[] }>;
    const m = new Map<string, NotificationItem[]>();
    for (const n of items) {
      const d = new Date(n.createdAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(n);
    }
    return Array.from(m.entries()).map(([day, list]) => {
      // Non-lues en tête dans chaque jour (décision produit 2026-06-05).
      // Tri stable : `list` est déjà en date décroissante (API) ; on ne fait
      // que remonter les non-lues avant les lues sans casser l'ordre interne.
      list.sort((a, b) => Number(a.isRead) - Number(b.isRead));
      const d = new Date(list[0]!.createdAt);
      const today = new Date();
      const isToday =
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate();
      const label = isToday
        ? "Aujourd'hui"
        : d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
      return { day, label, items: list };
    });
  }, [items]);

  return (
    <AppLayout>
      <PageHeader
        title="Notifications"
        subtitle={subtitle}
        actions={
          items && items.some((n) => !n.isRead) ? (
            <Button
              variant="secondary"
              leadingIcon={<CheckCheck className="h-4 w-4" />}
              onClick={handleReadAll}
            >
              Tout marquer lu
            </Button>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <Select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="w-auto text-xs"
        >
          <option value="">Tous types</option>
          {Object.entries(TYPE_LABELS).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </Select>
        <Select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="w-auto text-xs"
        >
          <option value="">Toutes priorités</option>
          {Object.entries(PRIORITY_LABELS).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </Select>
        <Select
          value={readFilter}
          onChange={(e) => setReadFilter(e.target.value)}
          className="w-auto text-xs"
        >
          <option value="">Lues + non lues</option>
          <option value="false">Non lues</option>
          <option value="true">Lues</option>
        </Select>
      </div>

      {error && <Banner tone="danger">{error}</Banner>}
      {items === null && !error && <Skeleton className="h-[400px] w-full" />}

      {items !== null && items.length === 0 && (
        <Card>
          <EmptyState title="Aucune notification" description="Rien à signaler pour le moment." />
        </Card>
      )}

      {grouped.length > 0 && (
        <div className="space-y-6">
          {grouped.map((g) => (
            <section key={g.day}>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                {g.label} · {g.items.length}
              </h2>
              <Card className="p-0">
                <ul>
                  {g.items.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => handleClick(n)}
                        className={cn(
                          'flex w-full items-start gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-gray-50',
                          // Non-lue : surlignage rouge (décision produit 2026-06-05).
                          !n.isRead && 'bg-danger/5',
                        )}
                      >
                        <span
                          className={cn(
                            'mt-1 h-2 w-2 shrink-0 rounded-full',
                            n.isRead ? 'bg-gray-300' : 'bg-danger',
                          )}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge tone={PRIORITY_TONES[n.priority]}>
                              {PRIORITY_LABELS[n.priority]}
                            </Badge>
                            <span className="text-xs font-medium text-text-muted">
                              {TYPE_LABELS[n.type] ?? n.type}
                            </span>
                          </div>
                          <div className="mt-1 text-sm font-medium text-brand">{n.title}</div>
                          {n.message && (
                            <div className="mt-0.5 line-clamp-2 text-xs text-text-primary">
                              {n.message}
                            </div>
                          )}
                          <div className="mt-0.5 text-[11px] text-text-muted">
                            {formatRelative(n.createdAt)}
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
